/**
 * Promo Discount Handler
 * Applies visual discount to prices for users who came via promo link
 */

class PromoDiscount {
  constructor() {
    this.init();
  }

  init() {
    // Check for discount on load and periodically (in case localStorage is being set)
    this.checkAndApply();
    
    // Also check after a short delay (for first-time visitors)
    setTimeout(() => this.checkAndApply(), 100);
    setTimeout(() => this.checkAndApply(), 500);

    // Re-apply when variant changes on product page
    document.addEventListener('variant:change', () => {
      console.log('Variant changed, reapplying promo discount...');
      this.resetAndReapply();
    });

    // Listen to Shopify's native variant change event
    if (typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        console.log('Shopify variant changed, reapplying promo discount...');
        this.resetAndReapply();
      });
    }

    // Watch for quick-add modal content changes
    this.observeQuickAddModals();
    
    // Watch for any DOM changes that might add new price elements
    this.observeDOMChanges();
  }

  resetAndReapply() {
    // Reset all applied flags to allow reapplication
    document.querySelectorAll('[data-promo-applied]').forEach(el => {
      el.removeAttribute('data-promo-applied');
    });
    setTimeout(() => this.checkAndApply(), 100);
  }

  observeQuickAddModals() {
    // Watch for when quick-add modal content is injected
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if this is a quick-add modal or contains price elements
            if (node.classList?.contains('quick-add-modal__content-info') || 
                node.querySelector?.('[data-promo-price-container]')) {
              console.log('Quick-add modal detected, applying promo discount...');
              setTimeout(() => this.checkAndApply(), 50);
            }
          }
        });
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  observeDOMChanges() {
    // Additional observer for dynamic content
    const priceObserver = new MutationObserver(() => {
      // Debounce to avoid too many calls
      clearTimeout(this.priceObserverTimeout);
      this.priceObserverTimeout = setTimeout(() => {
        const unappliedPrices = document.querySelectorAll('[data-promo-price-container]:not([data-promo-applied])');
        if (unappliedPrices.length > 0) {
          console.log(`Found ${unappliedPrices.length} unapplied prices, applying discount...`);
          this.checkAndApply();
        }
      }, 100);
    });

    // Observe specific areas where prices might be added
    const targets = document.querySelectorAll('.quick-add-modal, .product-form, main');
    targets.forEach(target => {
      if (target) {
        priceObserver.observe(target, {
          childList: true,
          subtree: true
        });
      }
    });
  }

  checkAndApply() {
    this.discountCode = localStorage.getItem('discountCode');
    this.discountPercentage = window.promoDiscountSettings?.percentage || 0;
    this.currencyFormat = window.promoDiscountSettings?.currencyFormat || '${{amount}}';
    
    if (this.discountCode && this.discountPercentage > 0) {
      this.applyDiscounts();
    }
  }

  applyDiscounts() {
    const priceContainers = document.querySelectorAll('[data-promo-price-container]');
    
    priceContainers.forEach(container => {
      // Check if already applied to avoid duplicate processing
      if (container.dataset.promoApplied === 'true') return;
      
      this.applyDiscountToContainer(container);
      container.dataset.promoApplied = 'true';
    });
  }

  applyDiscountToContainer(container) {
    const originalPrice = parseFloat(container.dataset.originalPrice);
    
    if (!originalPrice || originalPrice <= 0) return;

    // Calculate discounted price
    const discountedPrice = originalPrice * (1 - this.discountPercentage / 100);
    
    // Get formatted prices
    const originalPriceFormatted = this.formatMoney(originalPrice);
    const discountedPriceFormatted = this.formatMoney(discountedPrice);

    // Hide regular and sale price displays
    const regularDiv = container.querySelector('.price__regular');
    const saleDiv = container.querySelector('.price__sale');
    
    if (regularDiv) regularDiv.style.display = 'none';
    if (saleDiv) saleDiv.style.display = 'none';

    // Show and populate promo price
    const promoWrapper = container.querySelector('[data-promo-price-wrapper]');
    const promoOriginalPrice = container.querySelector('[data-promo-original-price]');
    const promoDiscountedPrice = container.querySelector('[data-promo-discounted-price]');

    if (promoWrapper && promoOriginalPrice && promoDiscountedPrice) {
      promoOriginalPrice.textContent = originalPriceFormatted;
      promoDiscountedPrice.textContent = discountedPriceFormatted;
      promoWrapper.style.display = 'block';
      
      // Add sale class to container if not already there
      if (!container.classList.contains('price--on-sale')) {
        container.classList.add('price--on-sale');
      }
    }
  }

  formatMoney(cents) {
    const moneyFormat = this.currencyFormat;
    
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (moneyFormat.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
    }

    return moneyFormat.replace(placeholderRegex, value);
  }
}

// Make it globally available
window.PromoDiscount = PromoDiscount;

// Initialize when script loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PromoDiscount();
  });
} else {
  new PromoDiscount();
}